/**
 * 작성자 : 한영광
 * 날짜 : 2023.06.13
 * 경로 : 근로자 관리 - 근로자 연명부
 */

import { useEffect, useState, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { IComCdList } from 'customTypes';
import { IUser, userState } from '../../../atoms';
import { FOREIGN_YN, INIT_WORKER_STATUS, LIST_COUNT, WORK_STATUS_SEARCH } from '../../../_constants';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { BtnBlue, BtnGhost } from '../../../components/Button';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import TuiGrid from '../../../components/Table/TuiGrid';
import SelectBox from '../../../components/SelectBox';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import ShortcutButton from '../../../components/button/ShortcutButton';
import InfoWorkerModal from '../../../components/Modal/InfoWorkerModal';
import SearchSelectBoxSm from '../../../components/SearchSelectBoxSm';
import EnrollmentWorkerModal from '../../../components/Modal/EnrollmentWorkerModal';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import { isMobile } from '../../../utils/checkMobile';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useSetSjcd } from '../../../utils/useSetSjcd';
import { trimObject } from '../../../utils/trimObject';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import { setComCdListState } from '../../../utils/setComCdListState';
import useSetJobtype from '../../../utils/useSetJobtype';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import useSetListCount from '../../../utils/useSetListCount';
import useSetNationality from '../../../utils/useSetNationality';
import { logPost } from '../../../services/log';
import i18n from '../../../translation/i18n';
import { apiGet } from '../../../services/_common';
import useMSite from '../../../hooks/useMSite';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import DownloadProgressModal from '../../../components/Modal/DownloadProgressModal';
import { showExcelButton } from '../../../utils/deviceAuth';

const Root = styled(ContentsContainerRoot)``;

const WorkerList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const size = useOutletContext<any>();
  const { siteJoinInfoList } = useSetSjcd();
  const { nationalityList } = useSetNationality();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue<IUser>(userState);
  const { eExcelYn, eListYn } = userInfo;
  const [tableState, setTableState] = useState<any[]>([]);
  const [orgTableState, setOrgTableState] = useState<any[]>([]);
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const wNameRef = useRef<HTMLInputElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [_nationalityList, _setNationalityList] = useState<any[]>(nationalityList);
  const [jikJongList, setJikJongList] = useState<any[]>([]);
  const [searchOption, setSearchOption] = useState({ wName: '', start: toDay, end: toDay });
  const [wWorkstatus, setWWorkstatus] = useState(INIT_WORKER_STATUS);
  const [sjCd, setSjCd] = useState({ type: 'sjCd', sjCd: '', cdName: '' });
  const [wForeignyn, setWForeignyn] = useState({ type: 'wForeignyn', wForeignyn: 'A', cdName: t('전체') });
  const [wNationality, setWNationality] = useState({ type: 'wNationality', wNationality: 'A', cdName: t('국적 전체') });
  const [wPrejobtype, setWPrejobtype] = useState({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
  const [wJobtype, setWJobtype] = useState({ type: 'wJobtype', wJobtype: '', cdName: '' });
  const { prejobtypeList, jobtypeList } = useSetJobtype(wPrejobtype.wPrejobtype);
  const [excelBtn, setExcelBtn] = useState(false);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [initiate, setinitiate] = useState('');
  const [rowKey, setRowKey] = useState('');
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);
  const [checkList, setCheckList] = useState([]); // 헤더체크된 로우리스트
  const [downloadProgress, setDownloadProgress] = useState({ isVisible: false, current: 0, total: 0 });
  const isMobileView = isMobile();

  useEffect(() => {
    setLoading(true);
    getWorkerAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '근로자 연명부',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  const addExcelData = [
    {
      header: t('국적'),
      name: 'wForeignynName',
      hidden: true,
    },
    {
      header: t('비자'),
      name: 'wVisa',
      hidden: true,
    },
    {
      header: t('혈액형'),
      name: 'wBlood',
      hidden: true,
    },
    {
      header: t('최저혈압'),
      name: 'wLowpress',
      hidden: true,
    },
    {
      header: t('최고혈압'),
      name: 'wHighpress',
      hidden: true,
    },
    {
      header: t('주소'),
      name: 'address',
      hidden: true,
    },
  ];

  useEffect(() => {
    setColumns(
      userInfo.prejobtypeYn === 'Y'
        ? [
            {
              header: t('근로자명'),
              name: 'wName',
              isPrivate: true,
              maskingType: 'name',
              sortable: true,
              minWidth: 130,
              renderer: { classNames: ['text_secondary'] },
            },
            {
              header: t('생년월일'),
              name: 'wBdate',
              hidden: true,
            },
            {
              header: t('협력업체'),
              name: 'sjName',
              sortable: true,
              filter: 'select',
              className: '',
              width: 160,
              renderer: { classNames: ['text_secondary'] },
            },
            {
              header: t('공종'),
              name: 'wPrejobtypeName',
              sortable: true,
              filter: 'select',
              minWidth: 120,
            },
            {
              header: t('직종'),
              name: 'wJobtypeName',
              sortable: true,
              filter: 'select',
              minWidth: 120,
            },
            {
              header: t('휴대폰번호'),
              name: 'wHnum',
              align: 'center',
              isPrivate: true,
              maskingType: 'tel',
              sortable: true,
              minWidth: 140,
            },
            {
              header: t('취업일자'),
              name: 'wJobdate',
              align: 'center',
              sortable: true,
              minWidth: 120,
            },
            {
              header: t('퇴직일자'),
              name: 'wRetiredate',
              align: 'center',
              sortable: true,
              minWidth: 120,
            },
            {
              header: t('등록일자'),
              name: 'wDate',
              align: 'center',
              sortable: true,
              minWidth: 160,
            },
            {
              header: t('최종 출역일자'),
              name: 'inoutDate',
              align: 'center',
              sortable: true,
              minWidth: 160,
            },
            {
              header: t('근로 상태'),
              name: 'wWorkstatusName',
              align: 'center',
              sortable: true,
              filter: 'select',
              minWidth: 120,
            },
            ...addExcelData,
          ]
        : [
            {
              header: t('근로자명'),
              name: 'wName',
              sortable: true,
              isPrivate: true,
              maskingType: 'name',
              minWidth: 130,
              renderer: { classNames: ['text_secondary'] },
            },
            {
              header: t('생년월일'),
              name: 'wBdate',
              hidden: true,
            },
            {
              header: t('협력업체'),
              name: 'sjName',
              sortable: true,
              filter: 'select',
              width: 200,
              renderer: { classNames: ['text_secondary'] },
            },
            {
              header: t('직종'),
              name: 'wJobtypeName',
              sortable: true,
              filter: 'select',
              minWidth: 120,
            },
            {
              header: t('휴대폰번호'),
              name: 'wHnum',
              align: 'center',
              isPrivate: true,
              maskingType: 'tel',
              sortable: true,
              minWidth: 140,
            },
            {
              header: t('취업일자'),
              name: 'wJobdate',
              align: 'center',
              sortable: true,
              minWidth: 120,
            },
            {
              header: t('퇴직일자'),
              name: 'wRetiredate',
              align: 'center',
              sortable: true,
              minWidth: 120,
            },
            {
              header: t('등록일자'),
              name: 'wDate',
              align: 'center',
              sortable: true,
              minWidth: 160,
            },
            {
              header: t('최종 출역일자'),
              name: 'inoutDate',
              align: 'center',
              sortable: true,
              minWidth: 160,
            },
            {
              header: t('근로 상태'),
              name: 'wWorkstatusName',
              align: 'center',
              sortable: true,
              filter: 'select',
              minWidth: 120,
            },
            ...addExcelData,
          ]
    );
  }, [userInfo.prejobtypeYn, i18n.language]);

  useEffect(() => {
    setWNationality({ type: 'wNationality', wNationality: 'A', cdName: t('국적 전체') });
    if (wForeignyn.wForeignyn === 'Y' || wForeignyn.wForeignyn === 'A') _setNationalityList(nationalityList);
    else _setNationalityList([]);
  }, [wForeignyn.wForeignyn, nationalityList]);

  useEffect(() => {
    setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
    if (wPrejobtype.wPrejobtype === '') setJikJongList([]);
  }, [wPrejobtype.wPrejobtype]);

  useEffect(() => {
    setJikJongList(jobtypeList);
  }, [jobtypeList]);

  useEffect(() => {
    setTableState(
      orgTableState.map((v: any) => {
        return {
          ...v,
          wWorkstatusName: t(v.wWorkstatusName),
          ...(eExcelYn === 'Y' && { wBdate: formatDateMask(v.wBdate) }),
          address: eExcelYn === 'Y' ? formatAddress(v.wAddress1) : `${v.wAddress1 ?? ''} ${v.wAddress2 ?? ''}`,
        };
      })
    );
  }, [i18n.language]);

  // 근로자 정보 리스트 조회 API
  const getWorkerAPI = async () => {
    const newSearchOption = {
      ...searchOption,
      wWorkstatus: wWorkstatus.wWorkstatus === 'A' ? '' : wWorkstatus.wWorkstatus,
      sjCd: sjCd.sjCd,
      wForeignyn: wForeignyn.wForeignyn === 'A' ? '' : wForeignyn.wForeignyn,
      wNationality: wNationality.wNationality === 'A' ? '' : wNationality.wNationality,
      wJobdate1: searchOption.start.replaceAll('-', ''),
      wJobdate2: searchOption.end.replaceAll('-', ''),
      wPrejobtype: wPrejobtype.wPrejobtype,
      wJobtype: wJobtype.wJobtype,
    };
    const { start, end, ...rest } = newSearchOption;
    const reqData = trimObject(rest); // searchOption에서 start, end값을 제외시키고 나머지값 trim
    const req = { ...reqData, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/worker', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      setOrgTableState(data.workerList);
      setTableState(
        data.workerList.map((v: any) => {
          return {
            ...v,
            wWorkstatusName: t(v.wWorkstatusName),
            ...(eExcelYn === 'Y' && { wBdate: formatDateMask(v.wBdate) }),
            address: eExcelYn === 'Y' ? formatAddress(v.wAddress1) : `${v.wAddress1 ?? ''} ${v.wAddress2 ?? ''}`,
            ...(userInfo?.photoSaveYn === 'Y' && { wRawName: v.wName }),
          };
        })
      );
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
    return res;
  };

  // 주소 문자열을 받아서 일부를 마스킹 처리하는 함수
  const formatAddress = (address: string): string => {
    if (!address) return '';

    const parts = address.split(' ');

    // 띄어쓰기가 2개 미만이면 그대로 반환
    if (parts.length < 3) return address;

    // 첫 번째와 두 번째 띄어쓰기까지의 부분을 가져옴
    const visiblePart = parts.slice(0, 2).join(' ');

    // 나머지 부분을 *로 마스킹
    const maskedPart = '*'.repeat(address.length - visiblePart.length);

    return `${visiblePart} ${maskedPart}`;
  };

  const formatDateMask = (dateString: string): string => {
    if (!dateString) return '';

    // YYYY-MM-DD 형식 검증
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      return dateString;
    }

    // 년도만 추출하고 나머지는 마스킹
    const year = dateString.substring(0, 4);
    return `${year}-**-**`;
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    // 검색 시 가상 키보드를 내려, 키보드 높이만큼 그리드 아래 흰 화면이 남는 문제 방지
    // (모바일은 resize 리스너가 없어 키보드가 올라간 상태로 계산된 그리드 높이가 복구되지 않음)
    if (isMobileView) (document.activeElement as HTMLElement | null)?.blur?.();
    setRowKey('');
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('날짜를 다시 입력하세요'));
        setSearchOption((prev) => ({ ...prev, end: '' }));
      } else {
        setLoading(true);
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setVisibleRangePicker(false);
        navigate({
          pathname: location.pathname,
          search: '',
        });
        getWorkerAPI();
      }
    }
  };

  const initiateSearchOptions = () => {
    setWWorkstatus(INIT_WORKER_STATUS);
    setSjCd({ type: 'sjCd', sjCd: '', cdName: '' });
    setWForeignyn({ type: 'wForeignyn', wForeignyn: 'A', cdName: t('전체') });
    setWNationality({ type: 'wNationality', wNationality: 'A', cdName: t('국적 전체') });
    setWPrejobtype({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
    setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev) => ({ ...prev, [el]: '', start: '', end: '' }));
    });
    setinitiate(`${Math.random()}`);
    initiateSearchOptions();
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  };

  const { mSiteQuery } = useMSite(userInfo.hCd, userInfo.sCd);
  const { data } = mSiteQuery;

  // 신규 등록 버튼 클릭
  const onClickNewWorker = () => {
    setOpenModal({ status: true, type: 'infoWorker', code: '' });
  };

  // 간편 등록 버튼 클릭
  const onClickSimpleWorker = () => {
    if (data && data.length > 0) {
      const mSiteCheck = data.filter((el: any) => el.useYn === 'Y' && el.delYn !== 'Y').map((el2: any) => (el2.wregYn === 'Y' ? { ...el2, checked: true } : { ...el2, checked: false }));
      setOpenModal({ status: true, type: 'workerEnrollment', mSiteCheck });
    } else setOpenModal({ status: true, type: 'workerEnrollment', mSiteCheck: null });
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '근로자 연명부',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    removeAfterPrint: false,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData?.wCd, aDate: dayjs().format('YYYY-MM-DD'), index: filteredIndex });
  };

  const renderCreateBtn = () => {
    if (auth.createAuth) {
      return (
        <>
          <BtnGhost onClick={onClickNewWorker}>
            <span className='material-symbols-rounded'>person_add</span> {t('신규')}
          </BtnGhost>
          <BtnGhost onClick={onClickSimpleWorker}>
            <span className='material-symbols-rounded'>group_add</span> {t('간편')}
          </BtnGhost>
        </>
      );
    }
    return null;
  };

  const onClickDownloadPhoto = async () => {
    if (checkList.length === 0) {
      toast.warning(t('근로자를 선택하세요.'));
      return;
    }
    const downloadImageWithFetch = async (url: string, filename: string) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 메모리 정리
        URL.revokeObjectURL(link.href);

        // 다운로드 완료 대기
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });

        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '근로자 연명부',
          action: '사진 다운로드',
          etc: `근로자명: ${filename}`,
        });
      } catch (error) {
        throw new Error(`Failed to download ${url}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    try {
      // wImg 주소가 없는 경우 다운로드 제외
      const list = checkList.filter((item: any) => item.wImg);
      if (list.length === 0) {
        toast.warning(t('근로자 이미지가 없습니다.'));
        return;
      }

      // 커스텀 다운로드 진행률 모달 표시
      setDownloadProgress({ isVisible: true, current: 0, total: list.length });

      for (let i = 0; i < list.length; i += 1) {
        const item = list[i] as any;
        const imageUrl = item.wImg;
        if (imageUrl) {
          const filename = `${item.wRawName}.jpg`;

          // eslint-disable-next-line no-await-in-loop
          await downloadImageWithFetch(imageUrl, filename);

          // 진행률 업데이트
          setDownloadProgress({ isVisible: true, current: i + 1, total: list.length });
        }
      }

      // 다운로드 완료
      setDownloadProgress({ isVisible: false, current: 0, total: 0 });
    } catch (error) {
      console.error('다운로드 중 오류 발생:', error);
      setDownloadProgress({ isVisible: false, current: 0, total: 0 });
      toast.error(t('다운로드 중 오류가 발생했습니다.'));
    }
  };

  const renderDownloadPhotoBtn = () => {
    if (userInfo.photoSaveYn === 'Y') {
      return <BtnGhost onClick={onClickDownloadPhoto}>{t('사진 다운로드')}</BtnGhost>;
    }
    return null;
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  // 근로자명 입력창에서 엔터입력시 검색
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (wNameRef.current) onClickSearch();
    }
  };
  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  const onClickCheckBox = (list: any) => {
    setCheckList(list);
  };

  return (
    <>
      <div className='content-container oneColumn'>
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                    <SelectBox
                      options={WORK_STATUS_SEARCH}
                      defaultOption={t('재직')}
                      state={wWorkstatus}
                      setState={setWWorkstatus}
                      stateKey='wWorkstatus'
                      initiateKey={wWorkstatus.wWorkstatus}
                      filterbar='filter-1-left'
                    />
                  </div>
                  <div className='inputForm-row withLabel'>
                    <label htmlFor='sjCd'>{t('협력업체')}</label>
                    <div className='inputForm-col'>
                      <SearchSelectBoxSm
                        options={siteJoinInfoList}
                        defaultOption={t('전체')}
                        state={sjCd}
                        setState={setSjCd}
                        stateKey='sjCd'
                        codeKey='cdName'
                        initiateKey={sjCd.sjCd}
                        filterbar='filter-1-left'
                        optionHeight='height-md'
                      />
                    </div>
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wForeignyn'>{t('외국인 여부')}</label>
                    <SelectBox
                      options={FOREIGN_YN}
                      defaultOption={t('전체')}
                      state={wForeignyn}
                      setState={setWForeignyn}
                      stateKey='wForeignyn'
                      initiateKey={wForeignyn.wForeignyn}
                      filterbar='filter-1-left'
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <SelectBox
                        options={_nationalityList}
                        defaultOption={t('국적 전체')}
                        state={wNationality}
                        setState={setWNationality}
                        stateKey='wNationality'
                        initiateKey={wNationality.wNationality}
                        filterbar='filter-1-center'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('취업일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  {userInfo.prejobtypeYn === 'Y' && (
                    <div className='inputForm-row'>
                      <div className='inputForm-col'>
                        <SearchSelectBoxSm
                          options={prejobtypeList}
                          defaultOption={t('공종 전체')}
                          state={wPrejobtype}
                          setState={setWPrejobtype}
                          stateKey='wPrejobtype'
                          codeKey='cdName'
                          initiateKey={initiate}
                          filterbar='filter-1-right'
                          comboWidth='expand-box-sm'
                          optionHeight='height-md'
                        />
                      </div>
                    </div>
                  )}
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <SearchSelectBoxSm
                        options={jikJongList}
                        defaultOption={t('직종 전체')}
                        state={wJobtype}
                        setState={setWJobtype}
                        stateKey='wJobtype'
                        codeKey='cdName'
                        initiateKey={wJobtype.wJobtype}
                        filterbar='filter-1-right'
                        comboWidth='expand-box-sm'
                        optionHeight='height-md'
                      />
                    </div>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('근로자명')}
                        label=''
                        type='text'
                        id='wName'
                        name='wName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={wNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                      />
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex-basic iconBtnGroup'>
                      {renderCreateBtn()}
                      {renderExcelBtn()}
                      {renderDownloadPhotoBtn()}
                      {/* {renderPrintBtn()} */}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('근로자명')}
                        label=''
                        type='text'
                        id='wName'
                        name='wName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={wNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        ico='search'
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    {renderCreateBtn()}
                    {renderExcelBtn()}
                  </div>
                </div>
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              onClickRow={onClickRow}
              onClickCheckBox={userInfo.photoSaveYn === 'Y' ? onClickCheckBox : undefined}
              scrollX
              height={tuiHeight}
              rowKey={rowKey}
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              rowHeight={48}
              frozenCount={frozenCount}
              setRowKey={setRowKey}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoWorker' && openModal.code !== undefined && (
          <InfoWorkerModal
            innerTabIndex={0}
            getReportListAPI={getWorkerAPI}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            setOpenModal={setOpenModal}
            auth={auth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
            listCount={Number(listCount[LIST_COUNT])}
          />
        )}
        {openModal.status && openModal.type === 'workerEnrollment' && (
          <EnrollmentWorkerModal getReportListAPI={getWorkerAPI} setOpenModal={setOpenModal} auth={auth} code={{ hCd: userInfo.hCd, sCd: userInfo.sCd }} mSiteCheck={openModal.mSiteCheck} />
        )}
      </Portal>

      {/* 다운로드 진행률 모달 */}
      <DownloadProgressModal isVisible={downloadProgress.isVisible} current={downloadProgress.current} total={downloadProgress.total} />

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('취업일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wWorkstatus'>{t('근로 상태')}</label>
                  <SelectBox
                    options={WORK_STATUS_SEARCH}
                    defaultOption={t('재직')}
                    state={wWorkstatus}
                    setState={setWWorkstatus}
                    stateKey='wWorkstatus'
                    initiateKey={wWorkstatus.wWorkstatus}
                    filterbar='filter-1-left'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='sjCd'>{t('협력업체')}</label>
                  <div className='inputForm-col'>
                    <SearchSelectBoxSm
                      options={siteJoinInfoList}
                      defaultOption={t('전체')}
                      state={sjCd}
                      setState={setSjCd}
                      stateKey='sjCd'
                      codeKey='cdName'
                      initiateKey={sjCd.sjCd}
                      filterbar='filter-1-left'
                      optionHeight='height-md'
                    />
                  </div>
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wForeignyn'>{t('외국인 여부')}</label>
                  <SelectBox
                    options={FOREIGN_YN}
                    defaultOption={t('전체')}
                    state={wForeignyn}
                    setState={setWForeignyn}
                    stateKey='wForeignyn'
                    initiateKey={wForeignyn.wForeignyn}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='wNationality'>{t('국적')}</label>
                  <div className='inputForm-col'>
                    <SelectBox
                      options={_nationalityList}
                      defaultOption={t('국적 전체')}
                      state={wNationality}
                      setState={setWNationality}
                      stateKey='wNationality'
                      initiateKey={wNationality.wNationality}
                      filterbar='filter-1-center'
                    />
                  </div>
                </div>
                {userInfo.prejobtypeYn === 'Y' && (
                  <div className='filter-option-row'>
                    <label htmlFor='sjCd'>{t('공종')}</label>
                    <SearchSelectBoxSm
                      options={prejobtypeList}
                      defaultOption={t('공종 전체')}
                      state={wPrejobtype}
                      setState={setWPrejobtype}
                      stateKey='wPrejobtype'
                      codeKey='cdName'
                      initiateKey={initiate}
                      filterbar='filter-1-right'
                      comboWidth='expand-box-sm'
                      optionHeight='height-md'
                    />
                  </div>
                )}
                <div className='filter-option-row'>
                  <label htmlFor='sjCd'>{t('직종')}</label>
                  <SearchSelectBoxSm
                    options={jikJongList}
                    defaultOption={t('직종 전체')}
                    state={wJobtype}
                    setState={setWJobtype}
                    stateKey='wJobtype'
                    codeKey='cdName'
                    initiateKey={wJobtype.wJobtype}
                    filterbar='filter-1-right'
                    comboWidth='expand-box-sm'
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default WorkerList;
